import React, { useState } from 'react';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';
import { gujaratDistricts } from '../../data/mockData';

/* ── TopoJSON asset ─────────────────────────────────────────────────────── */
const GEO_URL = '/gujarat-districts.topo.json';

/* ── Colour scale: 5 shades light → dark blue ─────────────────────────── */
const SHADES  = ['#DBEAFE', '#93C5FD', '#3B82F6', '#1D4ED8', '#1E3A8A'];
const NO_DATA = '#CBD5E1';

function getShade(value: number | undefined, min: number, max: number): string {
  if (value === undefined) return NO_DATA;
  const norm = (value - min) / (max - min || 1);
  return SHADES[Math.min(4, Math.floor(norm * 5))];
}

/* ── Name mapping: TopoJSON NAME_2 → mock data district name ───────────── */
const GEO_TO_MOCK: Record<string, string> = {
  'Ahmadabad':    'Ahmedabad',
  'Surat':        'Surat',
  'Vadodara':     'Vadodara',
  'Rajkot':       'Rajkot',
  'Junagadh':     'Junagadh',
  'Gandhinagar':  'Gandhinagar',
  'Anand':        'Anand',
  'Kheda':        'Kheda',
  'Mahesana':     'Mahesana',
  'Patan':        'Patan',
  'Banas Kantha': 'Banas Kantha',
  'Sabar Kantha': 'Sabar Kantha',
  'Panch Mahals': 'Panch Mahals',
  'Dahod':        'Dahod',
  'Narmada':      'Narmada',
  'Bharuch':      'Bharuch',
  'Kachchh':      'Kachchh',
  'Jamnagar':     'Jamnagar',
  'Porbandar':    'Porbandar',
  'Amreli':       'Amreli',
  'Bhavnagar':    'Bhavnagar',
  'Surendranagar':'Surendranagar',
  'Navsari':      'Navsari',
  'Valsad':       'Valsad',
  'The Dangs':    'The Dangs',
};

/* ── Utility: darken a hex colour ─────────────────────────────────────── */
function darken(hex: string, factor = 0.82): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const c = (v: number) =>
    Math.round(Math.min(255, v * factor)).toString(16).padStart(2, '0');
  return `#${c(r)}${c(g)}${c(b)}`;
}

/* ── Component ─────────────────────────────────────────────────────────── */
export default function GujaratMap() {
  const [hovered, setHovered]   = useState<string | null>(null);
  const [tooltip, setTooltip]   = useState<{ x: number; y: number; text: string } | null>(null);

  /* Build lookup: mock district name → data */
  const distMap = Object.fromEntries(gujaratDistricts.map(d => [d.name, d]));

  const values = gujaratDistricts.map(d => d.generationGWh);
  const minVal = Math.min(...values);
  const maxVal = Math.max(...values);

  return (
    <div className="relative w-full h-full flex flex-col">
      <ComposableMap
        /* Gujarat bounding box centre ≈ 71.5°E, 22.3°N */
        projection="geoMercator"
        projectionConfig={{ scale: 3800, center: [71.5, 22.4] }}
        style={{ width: '100%', height: '100%' }}
      >
        <Geographies geography={GEO_URL}>
          {({ geographies }) =>
            geographies.map(geo => {
              const geoName  = geo.properties.NAME_2 as string;
              const mockName = GEO_TO_MOCK[geoName] ?? geoName;
              const data     = distMap[mockName];
              const fill     = getShade(data?.generationGWh, minVal, maxVal);
              const isHover  = hovered === geoName;

              return (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  fill={isHover ? darken(fill) : fill}
                  stroke="#FFFFFF"
                  strokeWidth={0.5}
                  style={{
                    default: { outline: 'none', cursor: 'pointer' },
                    hover:   { outline: 'none', cursor: 'pointer' },
                    pressed: { outline: 'none' },
                  }}
                  onMouseEnter={e => {
                    setHovered(geoName);
                    const rect = (e.target as SVGPathElement)
                      .closest('svg')!.getBoundingClientRect();
                    const text = data
                      ? `${mockName}: ${data.generationGWh.toFixed(2)} GWh · PR ${data.prPercent.toFixed(1)}%`
                      : `${mockName}: No data`;
                    setTooltip({ x: e.clientX - rect.left + 12, y: e.clientY - rect.top - 36, text });
                  }}
                  onMouseMove={e => {
                    const rect = (e.target as SVGPathElement)
                      .closest('svg')!.getBoundingClientRect();
                    setTooltip(prev => prev
                      ? { ...prev, x: e.clientX - rect.left + 12, y: e.clientY - rect.top - 36 }
                      : null);
                  }}
                  onMouseLeave={() => { setHovered(null); setTooltip(null); }}
                />
              );
            })
          }
        </Geographies>
      </ComposableMap>

      {/* Floating tooltip */}
      {tooltip && (
        <div
          className="absolute z-50 px-2.5 py-1.5 rounded text-white text-xs shadow-lg pointer-events-none"
          style={{
            left: tooltip.x,
            top:  tooltip.y,
            background: 'rgba(15,23,42,0.90)',
            whiteSpace: 'nowrap',
          }}
        >
          {tooltip.text}
        </div>
      )}

      {/* Legend */}
      <div className="flex items-center justify-between mt-2 px-1">
        <div className="flex items-center gap-2">
          <span className="text-xs text-text-tertiary">Low</span>
          <div className="flex gap-0.5">
            {SHADES.map((c, i) => (
              <div key={i} className="w-7 h-2.5 rounded-sm" style={{ backgroundColor: c }} />
            ))}
          </div>
          <span className="text-xs text-text-tertiary">High</span>
          <span className="text-xs text-text-tertiary ml-1">(GWh today)</span>
        </div>
        <div className="flex items-center gap-3 text-xs text-text-tertiary">
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded-sm inline-block" style={{ background: NO_DATA }} />
            No data
          </span>
        </div>
      </div>
    </div>
  );
}
